import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as SectorsActions from './sectors.actions';
import { SectorService } from '../services/sector.service';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class SectorsEffects {
  private actions$ = inject(Actions);
  private sectorService = inject(SectorService);
  private router = inject(Router);
  private messageService = inject(MessageService);

  loadSectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SectorsActions.loadSectors),
      switchMap(({ pageNumber, pageSize }) =>
        this.sectorService.getSectors(pageNumber, pageSize).pipe(
          mergeMap((response) => {
            const data = response.data;
            const sectors = data?.items || [];
            return [
              SectorsActions.updatePagination({
                currentPage: data?.pageNumber,
                pageSize: data?.pageSize,
                totalItems: data?.totalCount,
              }),
              SectorsActions.loadSectorsSuccess({ sectors }),
            ];
          }),
          catchError((error) => {
            console.error('Error loading sectors:', error);
            return of(SectorsActions.loadSectorsFailure({ error: error.message }));
          })
        )
      )
    )
  );

  loadSector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SectorsActions.loadSector),
      switchMap(({ id }) =>
        this.sectorService.getSectorById(id).pipe(
          map((response) => {
            console.log('Load sector response:', response);
            const sector = response.data || response;
            return SectorsActions.loadSectorSuccess({ sector });
          }),
          catchError((error) => {
            console.error('Error loading sector:', error);
            return of(SectorsActions.loadSectorFailure({ error: error.message }));
          })
        )
      )
    )
  );

  createSector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SectorsActions.createSector),
      mergeMap(({ sector }) =>
        this.sectorService.createSector(sector).pipe(
          map((response) => {
            console.log('Create sector response:', response);
            const createdSector = response.data || response;
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Sector created successfully',
            });
            return SectorsActions.createSectorSuccess({ sector: createdSector });
          }),
          catchError((error) => {
            console.error('Error creating sector:', error);
            return of(SectorsActions.createSectorFailure({ error: error.message }));
          })
        )
      )
    )
  );

  updateSector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SectorsActions.updateSector),
      mergeMap(({ id, sector }) =>
        this.sectorService.updateSector(id, sector).pipe(
          map((response) => {
            console.log('Update sector response:', response);
            const updatedSector = response.data || response;
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Sector updated successfully',
            });
            return SectorsActions.updateSectorSuccess({ sector: updatedSector });
          }),
          catchError((error) => {
            console.error('Error updating sector:', error);
            return of(SectorsActions.updateSectorFailure({ error: error.message }));
          })
        )
      )
    )
  );

  deleteSector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SectorsActions.deleteSector),
      mergeMap(({ id }) =>
        this.sectorService.deleteSector(id).pipe(
          map(() => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Sector deleted successfully',
            });
            return SectorsActions.deleteSectorSuccess({ id });
          }),
          catchError((error) => {
            return of(SectorsActions.deleteSectorFailure({ error: error.message }));
          })
        )
      )
    )
  );

  loadTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SectorsActions.loadTemplates),
      switchMap(() =>
        this.sectorService.getTemplates().pipe(
          map((response) => {
            const templates = response.items || response.data?.items || [];
            console.log('Load templates response:', templates);
            return SectorsActions.loadTemplatesSuccess({ templates });
          }),
          catchError((error) => {
            console.error('Error loading templates:', error);
            return of(SectorsActions.loadTemplatesFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Redirect after successful operations
  redirectAfterSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SectorsActions.createSectorSuccess,
          SectorsActions.updateSectorSuccess,
          SectorsActions.deleteSectorSuccess
        ),
        tap(() => {
          this.router.navigate(['/sectors']);
        })
      ),
    { dispatch: false }
  );
}
